import { hub } from "@/lib/hub";
import { SiteError } from "@/lib/siteApi";
import { site } from "./current";
import type { FaqGroupRecord, MessageOverrides, Pages, PostRecord, ProjectRecord, ServiceRecord, VersionInfo } from "./types";

// Everything the TASMA dashboard can change, with the same names as the website's server actions
// (tasma-website app/admin/actions.ts), so the copied screens work unchanged. Each one calls the website's admin API;
// the website checks the person's permissions and applies the same rules and messages (its application/admin.ts).

export type Result = { ok: true; id?: string } | { ok: false; error: string };

async function run(work: () => Promise<{ ok: boolean; id?: string; error?: string }>): Promise<Result> {
  try {
    const answer = await work();
    return answer.ok ? { ok: true, id: answer.id } : { ok: false, error: answer.error ?? "That didn't work. Try again." };
  } catch (error) {
    if (error instanceof SiteError && error.status === 403) return { ok: false, error: "You don't have permission for this. Ask an owner." };
    return { ok: false, error: error instanceof Error ? error.message : "That didn't work. Try again." };
  }
}

const id = (value: string) => encodeURIComponent(value);

export const savePost = (input: Partial<PostRecord>) => run(() => site().post("/posts", input));
export const deletePost = (postId: string) => run(() => site().del(`/posts/${id(postId)}`));
export const reorderPosts = (ids: string[]) => run(() => site().post("/posts/order", { ids }));

export const saveProject = (input: Partial<ProjectRecord>) => run(() => site().post("/projects", input));
export const deleteProject = (projectId: string) => run(() => site().del(`/projects/${id(projectId)}`));
export const reorderProjects = (ids: string[]) => run(() => site().post("/projects/order", { ids }));

export const saveService = (input: Partial<ServiceRecord>) => run(() => site().post("/services", input));
export const deleteService = (serviceId: string) => run(() => site().del(`/services/${id(serviceId)}`));
export const reorderServices = (ids: string[]) => run(() => site().post("/services/order", { ids }));

/** On: visitors see only the coming-soon game. Off: the full website is live (within about 10 seconds). */
export const setComingSoon = (on: boolean) => run(() => site().put("/launch", { comingSoon: on }));

export const saveFaq = (groups: FaqGroupRecord[]) => run(() => site().put("/faq", groups));
export const savePages = (input: Pages) => run(() => site().put("/pages", input));
export const saveInterfaceText = (input: MessageOverrides) => run(() => site().put("/ui", input));

export const updateMedia = (mediaId: string, changes: { alt?: string; name?: string }) => run(() => site().patch(`/media/${id(mediaId)}`, changes));
/** Where a media file is used, so it isn't deleted while the site still shows it. */
export async function mediaUsage(url: string): Promise<string[]> {
  return (await site().get<{ usedBy?: string[] }>(`/media/usage?url=${encodeURIComponent(url)}`)).usedBy ?? [];
}
export const deleteMedia = (mediaId: string) => run(() => site().del(`/media/${id(mediaId)}`));

/** The earlier versions of a part of the site, newest first. */
export async function listVersions(collection: string): Promise<VersionInfo[]> {
  return (await site().get<{ versions?: VersionInfo[] }>(`/history/${id(collection)}`)).versions ?? [];
}
/** Puts an earlier version back. The version it replaces is kept too, so this can be undone the same way. */
export const restoreVersion = (collection: string, versionId: string) => run(() => site().post(`/history/${id(collection)}/${id(versionId)}/restore`));

export const updateLead = (leadId: string, status: "new" | "replied" | "archived") => run(() => site().patch(`/leads/${id(leadId)}`, { status }));
export const deleteLead = (leadId: string) => run(() => site().del(`/leads/${id(leadId)}`));

/** Takes a score off the leaderboards (with a reason only admins see), or puts it back. */
export const setScoreHidden = (scoreId: string, hidden: boolean, reason: string) => run(() => site().patch(`/scores/${id(scoreId)}`, { hidden, reason }));

/** Signs out of the hub (the copied menu's "Sign out" button submits a form to this). */
export async function logout() {
  await hub.logout().catch(() => {});
  window.location.assign("/login");
}
